import { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { Loader2, Send, AlertCircle, Bot, User, RefreshCcw } from 'lucide-react'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Textarea } from '../components/ui/textarea'
import { useToast } from '../components/ui/toast'
import {
  getChatStatus,
  sendChatMessage,
  getMobileUsers,
  getPromptPreview,
  type ChatMessage,
  type MobileUserSummary
} from '../lib/api/chat'
import { getSystemPrompts, type SystemPrompt } from '../lib/api/systemPrompts'

const MODEL_OPTIONS = [
  'openai/gpt-5-nano',
  'openai/gpt-5-mini',
  'x-ai/grok-4-fast',
  'google/gemini-2.5-flash-lite',
  'openai/gpt-oss-120b'
]

export const AskPage = () => {
  const [input, setInput] = useState('')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isConfigured, setIsConfigured] = useState<boolean | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [promptLoading, setPromptLoading] = useState(false)
  const [systemPrompts, setSystemPrompts] = useState<SystemPrompt[]>([])
  const [selectedPromptId, setSelectedPromptId] = useState<string | undefined>(undefined)
  const [selectedModel, setSelectedModel] = useState<string>('')
  const [defaultModel, setDefaultModel] = useState<string | undefined>(undefined)
  const [promptPreview, setPromptPreview] = useState<string>('')
  const [promptPreviewLoading, setPromptPreviewLoading] = useState(false)
  const [promptPreviewError, setPromptPreviewError] = useState<string | null>(null)
  const [mobileUsers, setMobileUsers] = useState<MobileUserSummary[]>([])
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined)
  const [usersLoading, setUsersLoading] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const { addToast } = useToast()

  // Check if the chat service is configured on mount
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const status = await getChatStatus()
        setIsConfigured(status.isConfigured)
        setDefaultModel(status.defaultModel)
        if (!status.isConfigured) {
          setError('Chat service is not configured. Please set the OPENROUTER_API_KEY environment variable.')
        }
      } catch {
        setError('Failed to check chat service status')
        setIsConfigured(false)
      }
    }
    checkStatus()
  }, [])

  useEffect(() => {
    const loadPrompts = async () => {
      setPromptLoading(true)
      try {
        const prompts = await getSystemPrompts()
        setSystemPrompts(prompts)
        const activePrompt = prompts.find((prompt) => prompt.isActive)
        setSelectedPromptId(activePrompt?.id)
      } catch (err) {
        console.error('Failed to load system prompts', err)
        addToast({
          title: 'Could not load system prompts',
          description: 'Chat may fail until a prompt is set.'
        })
      } finally {
        setPromptLoading(false)
      }
    }
    loadPrompts()
  }, [addToast])

  // Load mobile users for context selection
  useEffect(() => {
    const loadUsers = async () => {
      setUsersLoading(true)
      try {
        const users = await getMobileUsers()
        setMobileUsers(users)
      } catch (err) {
        console.error('Failed to load mobile users', err)
      } finally {
        setUsersLoading(false)
      }
    }
    loadUsers()
  }, [])

  // Sync model selection when prompt changes
  useEffect(() => {
    const selectedPrompt = systemPrompts.find((p) => p.id === selectedPromptId)
    if (selectedPrompt?.model) {
      setSelectedModel(selectedPrompt.model)
    }
  }, [selectedPromptId, systemPrompts])

  // Load prompt preview when prompt or user context changes
  useEffect(() => {
    if (isConfigured === null) return
    if (isConfigured === false) {
      setPromptPreview('')
      return
    }

    const loadPromptPreview = async () => {
      setPromptPreviewLoading(true)
      setPromptPreviewError(null)
      try {
        const preview = await getPromptPreview({
          systemPromptId: selectedPromptId || undefined,
          mobileUserId: selectedUserId || undefined
        })
        setPromptPreview(preview.prompt)
      } catch (err) {
        console.error('Failed to load prompt preview', err)
        setPromptPreview('')
        setPromptPreviewError('Could not load prompt preview')
      } finally {
        setPromptPreviewLoading(false)
      }
    }

    loadPromptPreview()
  }, [selectedPromptId, selectedUserId, isConfigured])

  // Auto-scroll to newest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isLoading])

  const handleSubmit = async () => {
    const trimmedInput = input.trim()
    if (!trimmedInput || isLoading) return

    const userMessage: ChatMessage = { role: 'user', content: trimmedInput }
    const updatedMessages = [...messages, userMessage]

    setMessages(updatedMessages)
    setInput('')
    setIsLoading(true)
    setError(null)

    try {
      const response = await sendChatMessage(updatedMessages, {
        systemPromptId: selectedPromptId || undefined,
        model: selectedModel || undefined,
        mobileUserId: selectedUserId || undefined
      })
      setMessages((prev) => [...prev, response.message])
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get response'
      setError(errorMessage)
      addToast({
        title: 'Error',
        description: errorMessage,
        variant: 'error'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  return (
    <div className="space-y-4">
      <header className="text-center">
        <p className="text-sm uppercase tracking-wide text-muted-foreground">Coaching Assistant</p>
        <h1 className="text-3xl font-semibold">Ask HikeStrong AI</h1>
        <p className="text-sm text-muted-foreground">
          Test the AI hiking coach. This simulates what mobile app users will experience.
        </p>
      </header>

      <Card className="mx-auto max-w-3xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            Chat
          </CardTitle>
          <CardDescription>
            Ask questions about hiking, training, gear, or trail recommendations.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status indicator */}
          {isConfigured === false && (
            <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error || 'Chat service is not available'}</span>
            </div>
          )}

          {/* Prompt selector */}
          <div className="flex flex-wrap items-center gap-3 rounded-md border border-muted/60 bg-muted/20 px-3 py-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <span>System prompt</span>
              {promptLoading ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : null}
            </div>
            <select
              value={selectedPromptId ?? ''}
              onChange={(e) => setSelectedPromptId(e.target.value || undefined)}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              disabled={promptLoading || isConfigured === false}
            >
              <option value="">Active prompt</option>
              {systemPrompts.map((prompt) => (
                <option key={prompt.id} value={prompt.id}>
                  {prompt.name} {prompt.isActive ? '(active)' : ''}
                </option>
              ))}
            </select>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setPromptLoading(true)
                getSystemPrompts()
                  .then((prompts) => {
                    setSystemPrompts(prompts)
                    const activePrompt = prompts.find((prompt) => prompt.isActive)
                    setSelectedPromptId((prev) => prev ?? activePrompt?.id ?? undefined)
                  })
                  .catch(() =>
                    addToast({
                      title: 'Could not refresh prompts',
                      description: 'Please try again.'
                    })
                  )
                  .finally(() => setPromptLoading(false))
              }}
              disabled={promptLoading}
              className="gap-2"
            >
              <RefreshCcw className="h-4 w-4" />
              Refresh
            </Button>
            <div className="text-xs text-muted-foreground">
              {systemPrompts.find((p) => p.id === selectedPromptId)?.name ||
                systemPrompts.find((p) => p.isActive)?.name ||
                'Using active prompt'}
            </div>
          </div>

          {/* Model selector */}
          <div className="flex flex-wrap items-center gap-3 rounded-md border border-muted/60 bg-muted/20 px-3 py-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <span>Model</span>
            </div>
            {(() => {
              const selectedPrompt = systemPrompts.find((p) => p.id === selectedPromptId)
              const promptModel = selectedPrompt?.model
              // Build unique model options list, including prompt's model if not in defaults
              const allModels = promptModel && !MODEL_OPTIONS.includes(promptModel)
                ? [promptModel, ...MODEL_OPTIONS]
                : MODEL_OPTIONS
              return (
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  disabled={isConfigured === false}
                >
                  <option value="">
                    {defaultModel ? `Use default (${defaultModel})` : 'Use default model'}
                  </option>
                  {allModels.map((model) => (
                    <option key={model} value={model}>
                      {model}{model === promptModel ? ' (from prompt)' : ''}
                    </option>
                  ))}
                </select>
              )
            })()}
            <div className="text-xs text-muted-foreground">
              {selectedModel
                ? `Using ${selectedModel}`
                : defaultModel
                ? `Using default: ${defaultModel}`
                : 'Using API default model'}
            </div>
          </div>

          {/* User context selector */}
          <div className="flex flex-wrap items-center gap-3 rounded-md border border-muted/60 bg-muted/20 px-3 py-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <span>User context</span>
              {usersLoading ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : null}
            </div>
            <select
              value={selectedUserId ?? ''}
              onChange={(e) => setSelectedUserId(e.target.value || undefined)}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              disabled={usersLoading || isConfigured === false}
            >
              <option value="">No user context</option>
              {mobileUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.displayName} {user.hasOnboardingData ? '' : '(no data)'}
                </option>
              ))}
            </select>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setUsersLoading(true)
                getMobileUsers()
                  .then((users) => setMobileUsers(users))
                  .catch(() =>
                    addToast({
                      title: 'Could not refresh users',
                      description: 'Please try again.'
                    })
                  )
                  .finally(() => setUsersLoading(false))
              }}
              disabled={usersLoading}
              className="gap-2"
            >
              <RefreshCcw className="h-4 w-4" />
              Refresh
            </Button>
            <div className="text-xs text-muted-foreground">
              {selectedUserId
                ? `Chatting as: ${mobileUsers.find((u) => u.id === selectedUserId)?.displayName}`
                : 'No user selected - generic responses'}
            </div>
          </div>

          {/* Prompt preview */}
          <div className="space-y-2 rounded-md border border-muted/60 bg-muted/10 p-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <span>Initial prompt preview</span>
              {promptPreviewLoading ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : null}
            </div>
            {promptPreviewError ? (
              <div className="text-xs text-destructive">{promptPreviewError}</div>
            ) : (
              <div className="max-h-52 overflow-y-auto rounded-md border border-muted/50 bg-background/60 p-3 text-xs font-mono whitespace-pre-wrap">
                {promptPreview || 'No prompt available'}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">
              Read-only preview of the active system prompt plus any injected mobile user context.
            </p>
          </div>

          {/* Messages container */}
          <div className="h-96 overflow-y-auto rounded-lg border bg-muted/20 p-4">
            {messages.length === 0 && !isLoading ? (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                <div className="text-center">
                  <Bot className="mx-auto mb-2 h-8 w-8 opacity-50" />
                  <p>Start a conversation with the hiking coach</p>
                  <p className="mt-1 text-xs">Try asking about trail recommendations or training tips</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`flex max-w-[85%] items-start gap-2 ${
                        message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                      }`}
                    >
                      <div
                        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                          message.role === 'user'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        }`}
                      >
                        {message.role === 'user' ? (
                          <User className="h-4 w-4" />
                        ) : (
                          <Bot className="h-4 w-4" />
                        )}
                      </div>
                      <div
                        className={`rounded-lg px-3 py-2 text-sm ${
                          message.role === 'user'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        }`}
                      >
                        {message.role === 'user' ? (
                          <p className="whitespace-pre-wrap">{message.content}</p>
                        ) : (
                          <div className="markdown-content">
                            <ReactMarkdown>{message.content}</ReactMarkdown>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Loading indicator */}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="flex items-start gap-2">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted">
                        <Bot className="h-4 w-4" />
                      </div>
                      <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Thinking...</span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input area */}
          <div className="flex gap-3">
            <Textarea
              placeholder="Ask about hiking, training, gear..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading || isConfigured === false}
              className="min-h-[60px] resize-none"
              rows={2}
            />
            <Button
              onClick={handleSubmit}
              disabled={!input.trim() || isLoading || isConfigured === false}
              className="shrink-0 self-end"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              <span className="ml-2 hidden sm:inline">Send</span>
            </Button>
          </div>

          {/* Suggested prompts for empty state */}
          {messages.length === 0 && isConfigured && (
            <div className="flex flex-wrap gap-2">
              <span className="text-xs text-muted-foreground">Try:</span>
              {[
                'What should I pack for a day hike?',
                'How do I train for a steep trail?',
                'Best hiking boots for beginners?'
              ].map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => setInput(suggestion)}
                  className="rounded-full border px-3 py-1 text-xs hover:bg-muted"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
